import { useContext, useState, useEffect, Fragment } from 'react';
import clienteAxios from '../../config/axios';
import Mensaje from 'components/Alertas/Mensaje';
import LineSpinner from 'components/spinners/LineSpinner';
import AuthContext from '../../context/autenticacion/authContext.js';
import styled, { keyframes } from 'styled-components';
import { useT } from 'i18n';

// Animaciones
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(20px); }
  to { opacity: 1; transform: translateX(0); }
`;

// Styled Components
const Container = styled.div`
  animation: ${fadeIn} 0.4s ease-out;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const InfoCard = styled.div`
  background: ${props => props.theme === 'white'
    ? '#fff'
    : 'rgba(12,14,19, 0.6)'};
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid ${props => props.theme === 'white'
    ? 'rgba(0, 0, 0, 0.1)'
    : 'rgba(227,168,43, 0.2)'};
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(227,168,43, 0.2);
  }
`;

const CardHeader = styled.div`
  padding: 1.5rem;
  background: linear-gradient(135deg, rgba(227,168,43, 0.15), rgba(23,27,36, 0.1));
  border-bottom: 1px solid ${props => props.theme === 'white'
    ? 'rgba(0, 0, 0, 0.05)'
    : 'rgba(227,168,43, 0.2)'};
  
  h1 {
    font-size: 1.3rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
    background: rgba(227,168,43,.14);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }
  
  p {
    color: ${props => props.theme === 'white' ? '#666' : 'rgba(255, 255, 255, 0.7)'};
    font-size: 0.9rem;
    line-height: 1.5;
    
    small {
      color: #e3a82b;
    }
  }
`;

const CardBody = styled.div`
  padding: 1.5rem;
  
  p {
    color: ${props => props.theme === 'white' ? '#666' : 'rgba(255, 255, 255, 0.7)'};
    font-size: 0.85rem;
    line-height: 1.6;
  }
`;

const FormContainer = styled.div`
  background: ${props => props.theme === 'white'
    ? '#fff'
    : 'rgba(12,14,19, 0.6)'};
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid ${props => props.theme === 'white'
    ? 'rgba(0, 0, 0, 0.1)'
    : 'rgba(227,168,43, 0.2)'};
  padding: 1.5rem;
  animation: ${slideIn} 0.4s ease;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 600;
    color: ${props => props.theme === 'white' ? '#333' : '#fff'};
    
    i {
      margin-right: 0.5rem;
      color: #e3a82b;
    }
  }
  
  input {
    width: 100%;
    padding: 0.875rem 1rem;
    border-radius: 12px;
    border: 1px solid ${props => props.theme === 'white'
      ? 'rgba(0, 0, 0, 0.2)'
      : 'rgba(227,168,43, 0.3)'};
    background: ${props => props.theme === 'white'
      ? '#f5f5f5'
      : 'rgba(12,14,19, 0.8)'};
    color: ${props => props.theme === 'white' ? '#333' : '#fff'};
    transition: all 0.3s ease;
    
    &:focus {
      outline: none;
      border-color: #e3a82b;
      box-shadow: 0 0 10px rgba(227,168,43, 0.3);
    }
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 0.875rem;
  border-radius: 50px;
  border: none;
  background: rgba(227,168,43,.14);
  color: #e3a82b;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(227,168,43, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const PasswordStrength = styled.div`
  margin-top: 0.5rem;
  font-size: 0.7rem;
  
  .strength-text {
    color: ${props => props.theme === 'white' ? '#666' : 'rgba(255, 255, 255, 0.6)'};
  }
  
  .strength-bar {
    height: 3px;
    border-radius: 3px;
    margin-top: 0.25rem;
    background: ${props => {
      switch(props.strength) {
        case 'weak': return '#ff5252';
        case 'medium': return '#ffc107';
        case 'strong': return '#e3a82b';
        default: return 'rgba(255,255,255,0.2)';
      }
    }};
    width: ${props => {
      switch(props.strength) {
        case 'weak': return '33%';
        case 'medium': return '66%';
        case 'strong': return '100%';
        default: return '0%';
      }
    }};
    transition: all 0.3s ease;
  }
`;

const PasswordReset = ({ jimf_theme }) => {
  const { t, ts } = useT();
  const authContext = useContext(AuthContext);
  const { listDatos } = authContext;

  const [spin, setSpin] = useState(false);
  const [mensaje, guardarMensaje] = useState('');
  const [mensajeopen, guardarMensajeOpen] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState('');

  const [usuario, guardarUsuario] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const { current_password, new_password, confirm_password } = usuario;
  const isWhiteTheme = jimf_theme === 'white';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Evaluar fortaleza de contraseña
  const evaluatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.match(/[A-Z]/)) strength++;
    if (password.match(/[0-9]/)) strength++;
    if (password.match(/[^A-Za-z0-9]/)) strength++;
    
    if (strength >= 3) return 'strong';
    if (strength >= 2) return 'medium';
    if (strength > 0) return 'weak';
    return '';
  };

  const handlePasswordChange = (e) => {
    const newPass = e.target.value;
    guardarUsuario({ ...usuario, [e.target.name]: newPass });
    if (e.target.name === 'new_password') {
      setPasswordStrength(evaluatePasswordStrength(newPass));
    }
  };

  const consultarApiReset = async () => {
    try {
      const respuesta = await clienteAxios.post('/api/client/changepassword', usuario);
      guardarMensajeOpen(true);
      guardarMensaje(ts(respuesta.data.message));
      
      // Limpiar formulario
      guardarUsuario({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
      setPasswordStrength('');
      
      listDatos();
    } catch (error) {
      guardarMensajeOpen(true);
      guardarMensaje(ts(error.response?.data?.message) || t('clave.errorCambiar'));
    } finally {
      setSpin(false);
    }
  };

  const onChange = (e) => {
    if (e.target.name === 'new_password') {
      handlePasswordChange(e);
    } else {
      guardarUsuario({ ...usuario, [e.target.name]: e.target.value });
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!current_password || !new_password || !confirm_password) {
      guardarMensajeOpen(true);
      guardarMensaje(t('clave.todosObligatorios'));
      return;
    }
    
    if (new_password !== confirm_password) {
      guardarMensajeOpen(true);
      guardarMensaje(t('clave.noCoincidenNuevas'));
      return;
    }
    
    if (new_password.length < 8) {
      guardarMensajeOpen(true);
      guardarMensaje(t('clave.minimo8'));
      return;
    }
    
    setSpin(true);
    consultarApiReset();
  };

  const Loader = spin ? <LineSpinner /> : <Fragment>{t('clave.cambiarBtn')}</Fragment>;

  // Texto de fortaleza
  const strengthText = {
    weak: t('clave.debil'),
    medium: t('clave.media'),
    strong: t('clave.fuerte')
  };

  return (
    <Container>
      <GridContainer>
        {/* Información */}
        <InfoCard theme={isWhiteTheme ? 'white' : 'dark'}>
          <CardHeader theme={isWhiteTheme ? 'white' : 'dark'}>
            <h1>{t('clave.privacidadTitulo')}</h1>
            <p>{t('clave.privacidadAyuda1')} <small>{t('clave.privacidadAyuda2')}</small> {t('clave.privacidadAyuda3')}</p>
          </CardHeader>
          <CardBody theme={isWhiteTheme ? 'white' : 'dark'}>
            <p>{t('clave.recomendacion1')} <small>{t('clave.recomendacion2')}</small></p>
            <p className="mt-3">
              <i className="tim-icons icon-bulb-63 mr-2" style={{ color: '#e3a82b' }} />
              {t('clave.consejo')}
            </p>
          </CardBody>
        </InfoCard>

        {/* Formulario */}
        <FormContainer theme={isWhiteTheme ? 'white' : 'dark'}>
          <form onSubmit={onSubmit}>
            <FormGroup theme={isWhiteTheme ? 'white' : 'dark'}>
              <label><i className="tim-icons icon-lock-circle" /> {t('clave.lblActual')}</label>
              <input
                type="password"
                id="current_password"
                name="current_password"
                placeholder={t('clave.phActual')}
                onChange={onChange}
                value={current_password}
                autoComplete="off"
                required
              />
            </FormGroup>

            <FormGroup theme={isWhiteTheme ? 'white' : 'dark'}>
              <label><i className="tim-icons icon-key-25" /> {t('clave.lblNueva')}</label>
              <input
                type="password"
                id="new_password"
                name="new_password"
                placeholder={t('clave.phNueva')}
                onChange={onChange}
                value={new_password}
                autoComplete="off"
                required
              />
              {new_password && (
                <PasswordStrength theme={isWhiteTheme ? 'white' : 'dark'} strength={passwordStrength}>
                  <div className="strength-text">
                    {t('clave.fortaleza')} {strengthText[passwordStrength] || ''}
                  </div>
                  <div className="strength-bar" />
                </PasswordStrength>
              )}
            </FormGroup>

            <FormGroup theme={isWhiteTheme ? 'white' : 'dark'}>
              <label><i className="tim-icons icon-check-2" /> {t('clave.lblConfirmar')}</label>
              <input
                type="password"
                id="confirm_password"
                name="confirm_password"
                placeholder={t('clave.phConfirmar')}
                onChange={onChange}
                value={confirm_password}
                autoComplete="off"
                required
              />
              {confirm_password && new_password !== confirm_password && (
                <small style={{ color: '#ff5252', marginTop: '0.5rem', display: 'block' }}>
                  <i className="tim-icons icon-alert-circle-exc mr-1" /> {t('clave.noCoinciden')}
                </small>
              )}
            </FormGroup>

            <SubmitButton type="submit" disabled={spin}>
              {Loader}
            </SubmitButton>
          </form>
        </FormContainer>
      </GridContainer>

      <div className="window-select-tipe mt-4">
        <h1><i className="tim-icons icon-lock-circle mr-2" /> {t('clave.actualizarMi')}</h1>
      </div>

      {mensajeopen && (
        <Mensaje
          open={true}
          mensaje={mensaje}
          guardarMensajeOpen={guardarMensajeOpen}
          guardarMensaje={guardarMensaje}
        />
      )}
    </Container>
  );
};

export default PasswordReset;